/**
 * The desert, which was here first.
 *
 * A spawner, weighted by the hour and by where you are standing. Nobody
 * authored an encounter: there is no trigger, no set piece, and no scene in
 * which a javelina arrives because you reached a place in the story. There is a
 * table of what lives where and when, and a roll every few minutes.
 *
 * That is the whole design and it is the reason it is worth having. A scorpion
 * in banquet storage at eleven at night is not a moment somebody wrote for you.
 * It is a scorpion, in the place scorpions are, at the hour scorpions are out,
 * and you can pick it up and throw it at somebody because the catalogue says
 * what it weighs.
 *
 *   rooms     where it turns up, by room id. The Caytons and the staff lot are
 *             on most of these lists, because a golf course at the edge of the
 *             Sonoran desert is where this stuff actually is — the property is
 *             the strange part, not the ground it sits on
 *   hours     the hours it is about, as [from, to] on a 24 hour clock, wrapping
 *   chance    the weight of one roll landing on it
 *   catch     whether it is something a person can pick up
 *   notice    feet at which a big one reacts to a person — you, or anybody
 *             else on the floor; it cannot tell a manager from a new hire
 *   charge    speed in metres per second when it comes at somebody
 *   herd      how many turn up together, as [fewest, most]. Absent is one
 *   minded    whether a person who sees one thinks it is somewhere it should
 *             not be. A coyote at the edge of the lot is the desert; a herd
 *             of javelina by the dock doors is a problem somebody has to deal
 *             with, and what they do about it is theirs to decide
 *   appetite  what food does to it. Absent is nothing — most of the desert has
 *             no interest in an orange. Otherwise:
 *               reach    feet at which food, on the ground or in somebody's
 *                        hands, draws it
 *               trot     metres per second, going to it
 *               follow   feet it hangs back at, behind somebody holding food
 *               closes   feet a second that gap shrinks, the longer it follows
 *               nearest  feet it will come to. Zero means all the way: it is
 *                        not following the person, it is following the food
 *   hears     what a noise does to it. Absent is nothing. Otherwise:
 *               reach    feet within which a noise that is not its own kind
 *                        makes it go and see, at most
 *               acuity   how many times further than a person it hears: a
 *                        noise a person hears at thirty feet, it hears at
 *                        thirty times this, up to `reach`
 *               prowl    metres per second, going there
 *   chance    a row with none is never rolled: it is only ever out because
 *             something put it there (`spawn`)
 *   ranges    rooms it may be led into or put out in but is never rolled in.
 *             Absent is nothing more than `rooms`. The desert sends it where
 *             `rooms` says; food, or a mission, can take it further
 */

/*
 * The cart road, which is three rooms because a room is one rectangle. Desert
 * on both sides of it, and the desert does not know where the asphalt is.
 */
const ROAD = ['road-1', 'road-2', 'road-3'];

export const WILDLIFE = [
  // --- the small ones, which are objects once you have hold of them --------
  {
    id: 'scorpion',
    kind: 'scorpion',
    rooms: ['storage', 'break', 'kitchen', 'dock', 'apron', 'parking', 'drive', 'wash'],
    hours: [19, 5],
    chance: 5,
    catch: true,
  },
  {
    id: 'lizard',
    kind: 'lizard',
    rooms: ['apron', 'dock', 'parking', 'caytons', 'drive', 'pool', ...ROAD, 'wash'],
    hours: [8, 18],
    chance: 6,
    catch: true,
  },
  {
    id: 'toad',
    kind: 'toad',
    rooms: ['apron', 'dock', 'caytons', 'pool', 'pool-n', 'pool-w', 'pool-e', 'wash'],
    hours: [20, 4],
    chance: 3,
    catch: true,
  },
  {
    id: 'quail',
    kind: 'quail',
    rooms: ['apron', 'caytons', 'sage', 'wash', ...ROAD],
    hours: [5, 10],
    chance: 4,
    catch: true,
    // A bird after crumbs. Close, slow, and never close enough to catch it
    // with food in your hand.
    appetite: { reach: 16, trot: 0.9, follow: 10, closes: 0.1, nearest: 5 },
  },
  {
    id: 'bat',
    kind: 'bat',
    rooms: ['apron', 'dock', 'parking', 'caytons', 'pool', 'lawn', 'drive'],
    hours: [19, 3],
    chance: 4,
    catch: true,
  },
  {
    id: 'tarantula',
    kind: 'tarantula',
    rooms: ['apron', 'storage', 'caytons', ...ROAD, 'wash'],
    hours: [20, 2],
    chance: 2,
    catch: true,
  },
  {
    id: 'roadrunner',
    kind: 'roadrunner',
    rooms: ['apron', 'parking', 'caytons', 'drive', ...ROAD, 'wash'],
    hours: [6, 19],
    chance: 3,
    catch: true,
    appetite: { reach: 20, trot: 1.6, follow: 12, closes: 0.1, nearest: 5 },
  },
  {
    id: 'rattlesnake',
    kind: 'rattlesnake',
    rooms: ['apron', 'caytons', ...ROAD, 'wash'],
    hours: [17, 23],
    chance: 2,
    catch: true,
  },

  // --- the big ones, which are not objects and do not care about you -------
  {
    id: 'coyote',
    name: 'Coyote',
    rooms: ['apron', 'parking', 'caytons', 'drive', 'service-n', 'service-e', ...ROAD, 'wash'],
    hours: [20, 5],
    chance: 2,
    catch: false,
    colour: 0x8a7a62,
    radius: 1.1,
    height: 2.2,
    /** Keeps its distance. Nothing happens; it is just there, and then it is not. */
    notice: 40,
    charge: 0,
    flee: 3.4,
    minded: false,
    /**
     * Interested, and not brave about it. It will trail you across the lot for
     * an orange and it will never come within a stone's throw to get one.
     */
    appetite: { reach: 34, trot: 2.2, follow: 30, closes: 0.15, nearest: 14 },
  },
  {
    id: 'javelina',
    name: 'Javelina',
    // The lawns too: the only patches of watered grass at the desert's edge,
    // and a herd that has found one comes back to it. And the drive and the
    // lane between them, which is how a herd from the lot gets to the lawn.
    rooms: ['apron', 'parking', 'caytons', 'lawn', 'sage', 'wren', 'drive', 'lane', 'service-e', 'service-n', ...ROAD, 'wash'],
    hours: [18, 6],
    chance: 2,
    catch: false,
    colour: 0x4a423a,
    radius: 1.4,
    height: 2.4,
    /**
     * Comes at you. Not because it is a boss and not because you did anything —
     * a javelina that thinks you are between it and somewhere it wants to be
     * will run at you, and the counterplay is the counterplay to being run at:
     * get behind something, get above something, or be somewhere else.
     */
    notice: 22,
    charge: 5.4,
    flee: 0,
    /** A squadron, not an animal. They are never about on their own. */
    herd: [3, 5],
    minded: true,
    /**
     * The strongest case in the table and the reason there is a table. A herd
     * will come the length of the apron for an orange on the ground, and one
     * held in your hand is followed — at a respectful distance to begin with,
     * and then not. Nearest is zero: the gap closes all the way, and what
     * arrives when it does is the same thing that arrives when one runs at you.
     */
    appetite: { reach: 70, trot: 2.4, follow: 24, closes: 0.25, nearest: 0 },
  },

  /*
   * Wild burros, which live up Burro Wash and come down it.
   *
   * A herd that stands. Nothing about a person makes it charge or run, so it
   * stays exactly where it is until something it wants is somewhere else, and
   * then it walks there. The desert only ever sends them into the wash; they
   * get onto the road because they were put there (`data/blockades.js`) or
   * because somebody with an apple walked them out onto it.
   */
  {
    id: 'burro',
    name: 'Wild Burro',
    rooms: ['wash'],
    ranges: [...ROAD, 'gate'],
    hours: [6, 19],
    chance: 1,
    catch: false,
    colour: 0x8b7d6b,
    radius: 2,
    height: 4.6,
    notice: 8,
    charge: 0,
    flee: 0,
    herd: [3, 4],
    minded: true,
    /**
     * Food on the ground, walked to and eaten; food in a hand, followed at a
     * few feet and never closer. Slow about all of it.
     */
    appetite: { reach: 45, trot: 1.1, follow: 8, closes: 0.05, nearest: 4 },
  },

  /*
   * A mountain lion, which the desert does not send: it has no `chance`, so no
   * roll ever lands on it. It is out only when something puts it out, and the
   * one thing that does is a guest stranded past the courts (`rescues.js`).
   *
   * Everything it does is two habits it shares with the rest of this table:
   * it comes at a person who gets close, like a javelina, and it goes to see
   * about a noise, which nothing else here does. So the golf cart, which is
   * loud, is the thing that brings it over, and a popper, which is louder,
   * is the thing that takes it somewhere else. Nobody fights it.
   */
  {
    id: 'mountainlion',
    name: 'Mountain Lion',
    rooms: ['caytons'],
    hours: [19, 5],
    chance: 0,
    catch: false,
    colour: 0xb08a5a,
    radius: 1.8,
    height: 2.8,
    notice: 26,
    charge: 6,
    flee: 0,
    minded: true,
    hears: { reach: 90, acuity: 3, prowl: 1.6 },
  },
];

export const WILDLIFE_BY_ID = Object.fromEntries(WILDLIFE.map((entry) => [entry.id, entry]));
